import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { orderService } from '../../services/orderService';
import { workFunctionService } from '../../services/workFunctionService';
import { useAuth } from '../../hooks/useAuth';
import OrderShopTasksCard from './OrderShopTasksCard';
import { formatDateDisplay, formatDateForInput } from '../../utils/dateUtils';
import { addBusinessHours, normalizeStaffCount } from '../../utils/scheduleDates';

function emptyLine() {
  return { key: `${Date.now()}-${Math.random()}`, work_function_id: '', estimated_hours: '', notes: '' };
}

function lineSum(lines) {
  return lines.reduce((acc, line) => {
    const h = parseFloat(line.estimated_hours);
    return acc + (Number.isFinite(h) && h > 0 ? h : 0);
  }, 0);
}

export default function WorkHoursTab({ orderId, order, readOnly = false, dueDate }) {
  const { isAdmin, isManagerOrAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [workFunctions, setWorkFunctions] = useState([]);
  const [estimatedHours, setEstimatedHours] = useState('');
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [staffCount, setStaffCount] = useState('1');
  const [calculateFromHours, setCalculateFromHours] = useState(false);
  const [lines, setLines] = useState([]);

  const hasBreakdown = lines.some((l) => l.work_function_id && parseFloat(l.estimated_hours) > 0);
  const breakdownTotal = useMemo(() => lineSum(lines), [lines]);
  const estimatedFunctionIds = useMemo(
    () =>
      [...new Set(
        lines
          .filter((l) => l.work_function_id && parseFloat(l.estimated_hours) > 0)
          .map((l) => parseInt(l.work_function_id, 10))
      )],
    [lines]
  );

  const effectiveTotalHours = useMemo(() => {
    if (hasBreakdown) return breakdownTotal;
    const h = parseFloat(estimatedHours);
    return Number.isFinite(h) && h > 0 ? h : null;
  }, [hasBreakdown, breakdownTotal, estimatedHours]);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    try {
      const [whRes, wfRes] = await Promise.all([
        orderService.getWorkHours(orderId),
        workFunctionService.getAll(),
      ]);
      const data = whRes?.data || whRes;
      setWorkFunctions(wfRes?.data || []);
      setEstimatedHours(data.estimated_hours != null ? String(data.estimated_hours) : '');
      setScheduleStart(formatDateForInput(data.schedule_start) || '');
      setScheduleEnd(formatDateForInput(data.schedule_end) || '');
      setStaffCount(String(normalizeStaffCount(data.staff_count)));
      setCalculateFromHours(Boolean(data.calculate_schedule_from_hours));
      setLines(
        (data.lines || []).map((line) => ({
          key: `line-${line.id}`,
          work_function_id: String(line.work_function_id),
          estimated_hours: String(line.estimated_hours),
          notes: line.notes || '',
        }))
      );
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load work hours');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!calculateFromHours || readOnly) return;
    if (!scheduleStart || effectiveTotalHours == null) return;
    const computed = addBusinessHours(scheduleStart, effectiveTotalHours, staffCount);
    if (computed) setScheduleEnd(computed);
  }, [calculateFromHours, scheduleStart, effectiveTotalHours, staffCount, readOnly]);

  const handleSave = async () => {
    if (!orderId || readOnly) return;
    const usedFunctionIds = lines
      .filter((l) => l.work_function_id && parseFloat(l.estimated_hours) > 0)
      .map((l) => l.work_function_id);
    if (new Set(usedFunctionIds).size !== usedFunctionIds.length) {
      setError('Each function can only appear once in the breakdown.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        schedule_start: scheduleStart || null,
        schedule_end: scheduleEnd || null,
        staff_count: normalizeStaffCount(staffCount),
        calculate_schedule_from_hours: calculateFromHours,
        schedule_dates_manual: !calculateFromHours,
        recalculate_dates: calculateFromHours,
        lines: lines
          .filter((l) => l.work_function_id && parseFloat(l.estimated_hours) > 0)
          .map((l) => ({
            work_function_id: parseInt(l.work_function_id, 10),
            estimated_hours: parseFloat(l.estimated_hours),
            notes: l.notes || null,
          })),
      };
      if (!hasBreakdown) {
        payload.estimated_hours =
          estimatedHours === '' || estimatedHours == null ? null : parseFloat(estimatedHours);
      }
      const res = await orderService.saveWorkHours(orderId, payload);
      const data = res?.data || res;
      setEstimatedHours(data.estimated_hours != null ? String(data.estimated_hours) : '');
      setScheduleStart(formatDateForInput(data.schedule_start) || '');
      setScheduleEnd(formatDateForInput(data.schedule_end) || '');
      setStaffCount(String(normalizeStaffCount(data.staff_count)));
      setCalculateFromHours(Boolean(data.calculate_schedule_from_hours));
      setLines(
        (data.lines || []).map((line) => ({
          key: `line-${line.id}`,
          work_function_id: String(line.work_function_id),
          estimated_hours: String(line.estimated_hours),
          notes: line.notes || '',
        }))
      );
      setMessage('Work hours saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save work hours');
    } finally {
      setSaving(false);
    }
  };

  const scheduleEndYmd = formatDateForInput(scheduleEnd);
  const dueDateYmd = formatDateForInput(dueDate);
  const scheduleEndAfterDue =
    scheduleEndYmd && dueDateYmd && scheduleEndYmd > dueDateYmd;

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Loading work hours…
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      {isManagerOrAdmin() ? (
        <OrderShopTasksCard
          orderId={orderId}
          workFunctions={workFunctions}
          estimatedFunctionIds={estimatedFunctionIds}
        />
      ) : null}

      {!scheduleStart || !scheduleEnd ? (
        <Alert severity="info">
          Set schedule start and end dates to show this order on the Schedule Gantt. Due date appears on the
          Schedule calendar.
        </Alert>
      ) : null}

      {scheduleEndAfterDue ? (
        <Alert severity="warning">
          Schedule end ({formatDateDisplay(scheduleEndYmd)}) is after the order due date (
          {formatDateDisplay(dueDateYmd)}).
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Staff
          </Typography>
          <TextField
            type="number"
            label="Number of staff"
            size="small"
            value={staffCount}
            disabled={readOnly}
            onChange={(e) => setStaffCount(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ maxWidth: 200 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Used with calculate from hours: each staff adds 8 hrs/day capacity (40-hr week).
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Total Hrs Estimate
          </Typography>
          <TextField
            type="number"
            label="Total hours"
            size="small"
            value={hasBreakdown ? breakdownTotal.toFixed(2) : estimatedHours}
            onChange={(e) => setEstimatedHours(e.target.value)}
            disabled={readOnly || hasBreakdown}
            inputProps={{ min: 0, step: 0.25 }}
            sx={{ maxWidth: 200 }}
          />
          {hasBreakdown ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Total is calculated from function breakdown below.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Function breakdown
            </Typography>
            {isAdmin ? (
              <Link component={RouterLink} to="/admin" variant="caption">
                Manage functions in Admin
              </Link>
            ) : null}
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Function</TableCell>
                <TableCell width={120}>Hours</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.key}>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={line.work_function_id}
                      disabled={readOnly}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === line.key ? { ...l, work_function_id: e.target.value } : l
                          )
                        )
                      }
                    >
                      <MenuItem value="">Select…</MenuItem>
                      {workFunctions.map((wf) => (
                        <MenuItem key={wf.id} value={String(wf.id)}>
                          {wf.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      value={line.estimated_hours}
                      disabled={readOnly}
                      inputProps={{ min: 0.25, step: 0.25 }}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) =>
                            l.key === line.key ? { ...l, estimated_hours: e.target.value } : l
                          )
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={line.notes}
                      disabled={readOnly}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l) => (l.key === line.key ? { ...l, notes: e.target.value } : l))
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {!readOnly ? (
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!readOnly ? (
            <Button
              startIcon={<AddIcon />}
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              Add function
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Schedule dates
          </Typography>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', lg: 'flex-start' }}
            flexWrap="wrap"
          >
            <TextField
              label="Schedule start"
              type="date"
              size="small"
              value={scheduleStart}
              disabled={readOnly}
              onChange={(e) => setScheduleStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Schedule end"
              type="date"
              size="small"
              value={scheduleEnd}
              disabled={readOnly || calculateFromHours}
              onChange={(e) => setScheduleEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={calculateFromHours}
                  disabled={readOnly}
                  onChange={(e) => setCalculateFromHours(e.target.checked)}
                />
              }
              label="Calculate from total hours"
              sx={{ mt: { xs: 0, lg: 0.5 }, ml: { lg: 1 } }}
            />
          </Stack>
          {calculateFromHours ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              End date = start + total hours ÷ ({normalizeStaffCount(staffCount)} staff × 8 hrs/day), Mon–Fri
              only.
            </Typography>
          ) : null}
          {!readOnly ? (
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 2 }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  );
}
